import React, { useState, useEffect, useRef } from 'react';
import './DiscussionPhase.css';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { useGame } from '../../contexts/GameContext';
import { moveToVotingPhase, startDiscussion, markDiscussionReady } from '../../firebase/services';

const DiscussionPhase = () => {
  const { lobby, playerId, isHost } = useGame();
  const [timeLeft, setTimeLeft] = useState(null);
  const [isEnding, setIsEnding] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const timerRef = useRef(null);

  const discussionMinutes = lobby?.settings?.discussionTimeMinutes ?? 3;
  const totalSeconds = discussionMinutes * 60;

  const discussionStartedAt = lobby?.discussionStartedAt;
  const isStarted = !!discussionStartedAt;

  const eliminatedIds = lobby?.eliminatedPlayerIds || [];
  const isEliminated = eliminatedIds.includes(playerId);
  const alivePlayers = lobby?.players?.filter(p => !eliminatedIds.includes(p.id)) || [];
  const eliminatedPlayers = lobby?.players?.filter(p => eliminatedIds.includes(p.id)) || [];
  const roundNumber = (lobby?.roundNumber || 0) + 1;

  const discussionReadyPlayers = lobby?.discussionReadyPlayers || [];
  const isPlayerReady = discussionReadyPlayers.includes(playerId);
  const notReadyPlayers = alivePlayers.filter(p => !discussionReadyPlayers.includes(p.id));

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!isStarted) {
      setTimeLeft(totalSeconds);
      return;
    }

    const startTime = discussionStartedAt?.toDate
      ? discussionStartedAt.toDate()
      : new Date(discussionStartedAt);

    const tick = () => {
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      const remaining = Math.max(0, totalSeconds - elapsed);
      setTimeLeft(remaining);
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isStarted, discussionStartedAt, totalSeconds]);

  const formatTime = (seconds) => {
    if (seconds == null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleStartDiscussion = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      await startDiscussion(lobby.code);
    } catch (error) {
      console.error('Error starting discussion:', error);
    } finally {
      setIsStarting(false);
    }
  };

  const handleEndDiscussion = async () => {
    if (isEnding) return;
    setIsEnding(true);
    try {
      await moveToVotingPhase(lobby.code);
    } catch (error) {
      console.error('Error moving to voting phase:', error);
      setIsEnding(false);
    }
  };

  const handleMarkReady = async () => {
    if (isMarkingReady || isPlayerReady) return;
    setIsMarkingReady(true);
    try {
      await markDiscussionReady(lobby.code, playerId);
    } catch (error) {
      console.error('Error marking discussion ready:', error);
    } finally {
      setIsMarkingReady(false);
    }
  };

  const getTimerClass = () => {
    if (timeLeft <= 30) return 'discussion-timer-critical';
    if (timeLeft <= 60) return 'discussion-timer-warning';
    return '';
  };

  if (!lobby) {
    return <div className="discussion-loading">Yükleniyor...</div>;
  }

  return (
    <div className="discussion-phase">
      <div className="discussion-header">
        <span className="discussion-round-badge">Tur {roundNumber}</span>
        <h2 className="discussion-title">Tartışma Zamanı</h2>
        <p className="discussion-description">
          Tüm oyuncular karttaki rollerine göre kimliklerini tartışırlar.
          Kurt takımındakiler köylü gibi davranmaya çalışır.
        </p>
      </div>

      {isEliminated && (
        <div className="discussion-eliminated-banner">
          <p>Elendin! Tartışmayı izleyebilirsin ama oy kullanamazsın.</p>
        </div>
      )}

      {lobby.zeusReveal && (
        <div className="discussion-zeus-reveal">
          <h3>Zeus'un Açığa Çıkardığı Kart</h3>
          <p className="discussion-zeus-reveal-text">
            <strong>{lobby.zeusReveal.playerName}</strong> oyuncusunun kartı:
          </p>
          <div className="discussion-zeus-reveal-card">
            <Card cardId={lobby.zeusReveal.cardId} revealed={true} />
          </div>
        </div>
      )}
      
      {!isStarted ? (
        <div className="discussion-start">
          <p>Tartışma henüz başlamadı.</p>
          {isHost ? (
            <Button 
              onClick={handleStartDiscussion}
              variant="primary"
              fullWidth={true}
              disabled={isStarting}
            >
              {isStarting ? 'Başlatılıyor...' : 'Tartışmayı Başlat'}
            </Button>
          ) : (
            <p>Kurucu tartışmayı başlattığında süre başlayacak.</p>
          )}
        </div>
      ) : (
        <div className="discussion-active">
          <div className={`discussion-timer ${getTimerClass()}`}>
            <div className="discussion-timer-visual">
              <div 
                className="discussion-timer-progress" 
                style={{ 
                  width: `${(timeLeft / totalSeconds) * 100}%` 
                }}
              ></div>
            </div>
            <div className="discussion-timer-display">
              <span className="discussion-timer-value">{formatTime(timeLeft)}</span>
              <span className="discussion-timer-label">kalan</span>
            </div>
          </div>
          
          <div className="discussion-players">
            <h3>Oyuncular</h3>
            <div className="discussion-players-grid">
              {alivePlayers.map(player => (
                <div key={player.id} className="discussion-player">
                  <div className="discussion-player-avatar">
                    {player.name.charAt(0)}
                  </div>
                  <div className="discussion-player-name">
                    {player.name}
                  </div>
                  {player.isSilenced && (
                    <div className="discussion-player-silenced">
                      <span title="Susturuldu">&#x1F910;</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {eliminatedPlayers.length > 0 && (
              <>
                <h3 className="discussion-eliminated-title">Elenenler</h3>
                <div className="discussion-players-grid">
                  {eliminatedPlayers.map(player => (
                    <div key={player.id} className="discussion-player discussion-player--eliminated">
                      <div className="discussion-player-avatar discussion-player-avatar--eliminated">
                        {player.name.charAt(0)}
                      </div>
                      <div className="discussion-player-name">
                        {player.name}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <div className="discussion-notes">
            <p>
              <strong>Not:</strong> Susturulmuş oyuncular bu turda konuşamaz, 
              ancak oylamaya katılabilirler.
            </p>
          </div>
          
          {timeLeft === 0 ? (
            <div className="discussion-time-up">
              <p className="discussion-time-up-text">Süre doldu!</p>
              {isHost && (
                <Button 
                  onClick={handleEndDiscussion}
                  variant="primary"
                  fullWidth={true}
                  disabled={isEnding}
                >
                  {isEnding ? 'Geçiliyor...' : 'Oylama Aşamasına Geç'}
                </Button>
              )}
            </div>
          ) : (
            <div className="discussion-controls">
              {!isEliminated && (
                <>
                  {!isPlayerReady ? (
                    <Button 
                      onClick={handleMarkReady}
                      variant="secondary"
                      fullWidth={true}
                      disabled={isMarkingReady}
                    >
                      {isMarkingReady ? 'Bekleniyor...' : 'Tartışmayı Bitir'}
                    </Button>
                  ) : (
                    <div className="discussion-ready-status">
                      <p className="discussion-ready-check">Tartışmayı bitirmek istediğini belirttin</p>
                    </div>
                  )}
                </>
              )}

              {discussionReadyPlayers.length > 0 && notReadyPlayers.length > 0 && (
                <div className="discussion-not-ready">
                  <span className="discussion-not-ready-label">
                    Bekleyen ({notReadyPlayers.length}):
                  </span>
                  <div className="discussion-not-ready-players">
                    {notReadyPlayers.map(player => (
                      <span key={player.id} className="discussion-not-ready-player">
                        {player.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscussionPhase;
